/**
 * Fouls, advantage, and the keeper rules that produce a restart. (§5.3, §5.5)
 *
 * Division of labour (deliberate, so it isn't rediscovered):
 *
 *   - player-controller.ts DETECTS contact-before-ball, CLASSIFIES the severity
 *     (Careless / Reckless / Excessive / DOGSO) and pushes a FOUL event. It owns
 *     "was this a foul and how bad", because that is pure tackle geometry.
 *   - goalkeeper.ts pushes a SIX_SECOND event when a keeper holds too long, and
 *     it DECLINES to handle a back-pass or a ball outside its box in the first
 *     place — so those offences are prevented at source, not whistled after.
 *   - THIS file is the referee's consequence: it consumes those events (raised
 *     earlier in the SAME tick; step.ts clears the array each tick, so nothing
 *     is ever reprocessed), plays or pulls back advantage, and stages the free
 *     kick / penalty.
 *
 * CARDS ARE DISABLED (owner, 2026-08-17: "remove the red card thing and yellow
 * card from the game give only fouls"). Every severity now earns the same
 * restart — a free kick, or a penalty when the offence was inside the
 * offender's own area — and NEVER a booking or a sending-off:
 *   Careless / Reckless / Excessive / DOGSO → free kick (or penalty), no card
 *
 * SEVERITY ITSELF IS STILL TRACKED, for two things that have nothing to do
 * with cards: an Excessive/DOGSO foul still denies advantage (a serious foul
 * is still whistled at once rather than played through, §5.3), and the
 * house-rule PUNCH/KICK offence counter still escalates a 4th violent-conduct
 * offence to a penalty (handleViolentConduct). `applyCard` is the one place
 * that used to turn a severity into a booking; it is kept as a named no-op —
 * reporting `RefereeCard.None` always — rather than deleted, so every call
 * site keeps reading the honest field instead of a severity a consumer might
 * mis-derive a card from (see ui/banners.ts and render/refSignal.ts, which
 * had to be fixed for exactly that reason).
 *
 * ONE THING THIS FILE IS STILL DELIBERATE ABOUT (§5.6):
 *
 *   THE REFEREE IS NOT INSTANT. Detection → restart used to run in one tick,
 *   so the whistle landed on the same frame as the tackle. A decided foul now
 *   waits `pendingWhistleTicks` (REFEREE_REACTION_MIN_MS..MAX_MS, drawn from
 *   the sim RNG) before the set piece is staged. See armWhistle /
 *   runPendingWhistle, and MatchState.pendingWhistleTicks for why it is on
 *   the hash.
 */

import type { MatchState, RuleEvent, StepResult, WorldState } from '../types.js';
import { FoulSeverity, MatchPhase, MAX_PLAYERS, RefereeCard } from '../types.js';
import {
  ADVANTAGE_WINDOW_S,
  PENALTY_AREA_LENGTH,
  PENALTY_AREA_WIDTH,
  PENALTY_SPOT_DISTANCE,
  PUNCH_KICK_OFFENSE_LIMIT,
  REFEREE_REACTION_MAX_MS,
  REFEREE_REACTION_MIN_MS,
  SIMULATION_TICK_RATE,
  STOPPAGE_FOUL_S,
  VAR_CHECK_S,
} from '../constants.js';
import { pcg32Float } from '../../math/pcg32.js';
import { addStoppage, attackDir, beginRestart, defendGoalX } from './fsm.js';

// ---------------------------------------------------------------------------
// Possession + geometry helpers
// ---------------------------------------------------------------------------

/**
 * True if `team` still "has" the ball for advantage purposes — either a
 * player of theirs is in live possession, OR the ball is merely LOOSE
 * (in flight on a pass, or bouncing off a touch) and their own player was
 * the last to play it.
 *
 * ⚠ THE BUG THIS EXISTS TO KILL (owner, 2026-08-23: "when the foul happens
 * even though the ball is with other player its comes here"). This used to
 * require LIVE possession (`possessedBy >= 0`) every single tick of the
 * advantage window — but a ball in flight between two of the fouled team's
 * OWN players (any pass, any dribble touch bouncing ahead of a runner) is
 * `possessedBy === -1` for the ticks it takes to arrive, same as a genuine
 * turnover. `runAdvantage` read that as "attack broke down" and pulled the
 * restart back mid-pass — the fouled team could complete an entirely clean
 * passing move and still watch the ball get yanked back to the original
 * foul spot the instant it left the passer's foot. CONFIRMED (advantage-
 * pullback-diagnostic.test.ts): a loose ball still last-touched by the
 * fouled team flipped `match.phase` out of InPlay every time.
 *
 * `lastTouchedTeam` already survives exactly this in-flight window (it only
 * changes on a real touch), so checking it instead of live possession
 * distinguishes "still our ball, just in the air" from "the other team
 * actually has it now" — the latter changes `lastTouchedBy` the instant an
 * opponent gets a touch, which is the genuine turnover this check exists to
 * catch.
 */
function teamInPossession(state: WorldState, team: number): boolean {
  const b = state.ball;
  if (b.possessedBy >= 0) return state.players.team[b.possessedBy] === team;
  return b.lastTouchedTeam === team;
}

/**
 * A foul is worth playing an advantage over only if the fouled team keeps the
 * ball AND is already in the attacking half — a "promising attack" (§5.3). A
 * foul in your own half just gets the free kick.
 */
function offersAdvantage(state: WorldState, victimTeam: number): boolean {
  const b = state.ball;
  if (b.possessedBy < 0) return false;
  if (state.players.team[b.possessedBy] !== victimTeam) return false;
  const dir = attackDir(state, victimTeam);
  return dir * state.players.px[b.possessedBy]! > 0;
}

/** Is (x,z) inside the penalty area `team` DEFENDS? (§5.3, penalty test) */
function insideOwnPenaltyArea(state: WorldState, team: number, x: number, z: number): boolean {
  const goalX = defendGoalX(state, team);
  return Math.abs(x - goalX) <= PENALTY_AREA_LENGTH && Math.abs(z) <= PENALTY_AREA_WIDTH * 0.5;
}

/** Penalty-spot X for the goal `team` defends (z is always the pitch centre). */
function penaltySpotX(state: WorldState, team: number): number {
  const goalX = defendGoalX(state, team);
  // Move PENALTY_SPOT_DISTANCE toward the centre of the pitch.
  return goalX > 0 ? goalX - PENALTY_SPOT_DISTANCE : goalX + PENALTY_SPOT_DISTANCE;
}

// ---------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------

/**
 * CARDS ARE DISABLED (owner, 2026-08-17). This used to book/dismiss a player by
 * severity — Reckless a yellow (second → red), Excessive/DOGSO a straight red,
 * each with its own added stoppage time (STOPPAGE_CARD_S) — and is kept as a
 * named no-op, always returning `RefereeCard.None` and never touching
 * `p.yellows`/`p.sentOff`, so `handleFoul`/`handleViolentConduct` below did not
 * need restructuring: the decision point stayed exactly where it was, it just
 * always decides "no card" now.
 */
function applyCard(_state: WorldState, _fouler: number, _severity: FoulSeverity): RefereeCard {
  return RefereeCard.None;
}

/**
 * Stamp the card onto the event a consumer will read. (§5.3)
 *
 * With cards disabled `card` is always `RefereeCard.None` — the honest field,
 * and the field `ui/banners.ts` / `render/refSignal.ts` / the server's
 * ref-signal broadcast now all read instead of guessing a card off `severity`
 * (severity alone used to draw a yellow for what was actually a red on a
 * second bookable offence; with no cards at all that trap cannot recur, but
 * the fields stay honest on principle).
 */
function reportCard(ev: RuleEvent, card: RefereeCard): void {
  ev.card = card;
}

// ---------------------------------------------------------------------------
// Awarding the restart
// ---------------------------------------------------------------------------

/**
 * Stop play and stage the set-piece the foul earns: a penalty if the offence was
 * inside the offender's own area, otherwise a free kick on the spot. The card is
 * NOT applied here — it was applied when the foul was first detected, so this is
 * safe to call from the advantage pull-back path too.
 *
 * This is the WHISTLE MOMENT. Everything that must not happen instantly (the
 * referee's reaction) happens before we get here; everything that follows the
 * whistle (the VAR check on a penalty) is staged from here.
 */
function awardFoulRestart(
  state: WorldState,
  fouler: number,
  victimTeam: number,
  x: number,
  z: number,
): void {
  addStoppage(state, STOPPAGE_FOUL_S);
  clearPendingWhistle(state.match); // play is stopping now; nothing is still pending
  const foulerTeam = fouler >= 0 ? state.players.team[fouler]! : 1 - victimTeam;
  if (insideOwnPenaltyArea(state, foulerTeam, x, z)) {
    beginRestart(state, MatchPhase.Penalty, victimTeam, penaltySpotX(state, foulerTeam), 0);
    runVarCheck(state);
  } else {
    beginRestart(state, MatchPhase.FreeKick, victimTeam, x, z);
  }
}

/**
 * VAR (§5.6) — the review a penalty award gets in competitive modes.
 *
 * `VAR_CHECK_S` existed as a constant nothing consumed, which is a lie about
 * what the game does; this is the smallest honest consumption of it. The
 * PENALTY award is the archetypal VAR check and the only decision this module
 * makes that is worth stopping the world for, so the review holds the restart
 * CEREMONY open for VAR_CHECK_S — the players are already staging, the taker
 * cannot strike until Setup→Ready, and the WHISTLE event fires on that flip, so
 * the check is visibly a pause before the kick rather than a frozen frame. The
 * same seconds go onto the clock, which is exactly what `MatchState.addedTime`
 * documents ("injuries/subs/goals/VAR/cards").
 *
 * Gated on `config.varEnabled`, which defaults to FALSE (world.ts
 * createMatchConfig) — so every existing mode is byte-identical and this is
 * opt-in. NOT applied to red cards or goals: those are the other two things a
 * real VAR reviews, but goals are ball-out.ts's and fsm.ts's, not this module's,
 * and pretending to review something we do not model would be the same lie in a
 * new place. Deliberately scoped, and stated so the next agent knows it is a
 * choice rather than an omission.
 *
 * Must be called AFTER beginRestart: beginRestart writes stageTimer through
 * sizeSetupWindowForWalkIn, so anything added before it is overwritten.
 */
function runVarCheck(state: WorldState): void {
  if (!state.config.varEnabled) return;
  state.match.stageTimer += VAR_CHECK_S;
  addStoppage(state, VAR_CHECK_S);
}

// ---------------------------------------------------------------------------
// Referee reaction — the whistle is not instant (§5.6)
// ---------------------------------------------------------------------------

/** Drop any pending whistle. */
function clearPendingWhistle(m: MatchState): void {
  m.pendingWhistleTicks = 0;
  m.pendingWhistleFouler = -1;
  m.pendingWhistleTeam = -1;
  m.pendingWhistleX = 0;
  m.pendingWhistleZ = 0;
}

/**
 * How long the referee takes to blow, in WHOLE TICKS.
 *
 * Ticks, not milliseconds: the countdown is hashed state and a fractional
 * seconds accumulator would make the stop tick depend on float accumulation
 * order. Drawn from the shared sim RNG (`pcg32Float`) — never `Math.random`,
 * never a wall clock (§15.3, lint-enforced).
 *
 * At SIMULATION_TICK_RATE 60 and 200..400ms that is a uniform 12..24 ticks
 * inclusive. Floored to at least 1 tick, because a 0-tick "delay" is the defect
 * this exists to fix.
 */
function refereeReactionTicks(state: WorldState): number {
  const minTicks = Math.max(1, Math.round((REFEREE_REACTION_MIN_MS / 1000) * SIMULATION_TICK_RATE));
  const maxTicks = Math.max(minTicks, Math.round((REFEREE_REACTION_MAX_MS / 1000) * SIMULATION_TICK_RATE));
  const drawn = minTicks + Math.floor(pcg32Float(state.rng) * (maxTicks - minTicks + 1));
  return drawn > maxTicks ? maxTicks : drawn; // pcg32Float is [0,1); belt and braces
}

/**
 * The referee has decided to give this foul — hold the whistle for a human
 * beat, then stage the restart.
 *
 * Only ever reached on the NO-ADVANTAGE path (see handleFoul), so this never
 * stacks on ADVANTAGE_WINDOW_S: a foul is either played on for 3s or whistled
 * in ~0.3s, never 3.3s.
 *
 * An offence during an existing pending whistle does NOT re-arm it. The referee
 * is already bringing play back for the FIRST thing he saw, and re-arming would
 * let a scramble push the whistle indefinitely further away. The later offence
 * still gets its card — applyCard ran before we were called.
 */
function armWhistle(
  state: WorldState,
  fouler: number,
  victimTeam: number,
  x: number,
  z: number,
): void {
  const m = state.match;
  if (m.pendingWhistleTicks > 0) return;
  m.pendingWhistleTicks = refereeReactionTicks(state);
  m.pendingWhistleFouler = fouler;
  m.pendingWhistleTeam = victimTeam;
  m.pendingWhistleX = x;
  m.pendingWhistleZ = z;
}

/**
 * Count a pending whistle down and blow it when it expires. (§5.6)
 *
 * Runs FIRST in evaluateFoulsAndCards, so the tick a foul is whistled is settled
 * before anything new is looked at.
 *
 * If play has already stopped for another reason inside the reaction window
 * (a goal, the ball leaving the field), the pending whistle is DROPPED rather
 * than held: the spot it was going to restart from is stale the moment the phase
 * changed, and resurrecting a free kick after a throw-in has been staged is a
 * worse lie than losing a foul in a ≤0.4s window. Documented because it is a
 * real, if rare, behavioural choice.
 */
function runPendingWhistle(state: WorldState): void {
  const m = state.match;
  if (m.pendingWhistleTicks <= 0) return;
  if (m.phase !== MatchPhase.InPlay) {
    clearPendingWhistle(m);
    return;
  }
  m.pendingWhistleTicks--;
  if (m.pendingWhistleTicks > 0) return;
  const fouler = m.pendingWhistleFouler;
  const victimTeam = m.pendingWhistleTeam;
  const x = m.pendingWhistleX;
  const z = m.pendingWhistleZ;
  clearPendingWhistle(m);
  awardFoulRestart(state, fouler, victimTeam, x, z);
}

function handleFoul(state: WorldState, out: StepResult, ev: RuleEvent): void {
  const m = state.match;
  const fouler = ev.player;
  const victimTeam = ev.team; // reportFoul sets team to the FOULED side
  const severity = ev.severity ?? FoulSeverity.Careless;
  const x = ev.x ?? 0;
  const z = ev.z ?? 0;

  // Card first, always at the moment of the offence — then report the card that
  // was actually issued on the event the client reads (a second yellow is a red).
  reportCard(ev, applyCard(state, fouler, severity));

  // Advantage only for non-dismissal fouls with a live promising attack (§5.3).
  // NOTE the ordering: advantage is decided on the ORIGINAL severity, read into
  // `severity` above, before reportCard could have escalated it — a second
  // yellow is still a Reckless CHALLENGE and may still be played through.
  const severe = severity === FoulSeverity.Excessive || severity === FoulSeverity.DOGSO;
  if (m.pendingWhistleTicks > 0) return; // already stopping play for an earlier foul
  if (!severe && offersAdvantage(state, victimTeam)) {
    m.advantageTicks = Math.round(ADVANTAGE_WINDOW_S * SIMULATION_TICK_RATE);
    m.pendingFoulPlayer = fouler;
    m.pendingFoulSeverity = severity;
    m.restartTeam = victimTeam;
    m.restartX = x;
    m.restartZ = z;
    out.events.push({ type: 'ADVANTAGE', tick: state.tick, team: victimTeam, player: fouler, x, z });
    return; // play on
  }

  // No advantage → the referee blows, but not on this tick (§5.6).
  armWhistle(state, fouler, victimTeam, x, z);
}

/**
 * Consume a house-rule PUNCH/KICK offence. (user "funny addon", 2026-07-17)
 *
 * Always a foul — there is no clean version, and no advantage is ever played
 * (you don't wave play on after a player throws hands). Card first (violent
 * conduct, a straight yellow minimum via the shared severity machinery), then
 * the per-player offence counter escalates:
 *
 *   - offences 1..3 → a direct free kick at the spot (already a penalty if the
 *     strike happened inside the offender's own box, via awardFoulRestart).
 *   - the 4th (count EXCEEDS PUNCH_KICK_OFFENSE_LIMIT) → a PENALTY to the
 *     victim's team wherever it happened, then the counter resets to 0.
 *
 * This is §5.3's "track per-player foul count; escalate" pushed to a house
 * extreme. See player-controller.ts startViolentConduct and DECISIONS.md D3.
 */
function handleViolentConduct(state: WorldState, ev: RuleEvent): void {
  // NOT routed through armWhistle: the reaction delay models a referee reading a
  // TACKLE — did he get the ball, was it careless or reckless. A player throwing
  // hands is unmissable and there is nothing to read, so violent conduct stops
  // play at once, as it did before. Stated here so the asymmetry is a decision.
  const p = state.players;
  const fouler = ev.player;
  const victimTeam = ev.team; // the event's team is the struck player's side
  const severity = ev.severity ?? FoulSeverity.Reckless;
  const x = ev.x ?? 0;
  const z = ev.z ?? 0;

  // Card at the moment of the offence, always — same as any other foul, and
  // reported the same way (a punch that earns a second yellow reads as a red).
  reportCard(ev, applyCard(state, fouler, severity));

  if (fouler >= 0) {
    p.punchKickOffenses[fouler] = p.punchKickOffenses[fouler]! + 1;
    if (p.punchKickOffenses[fouler]! > PUNCH_KICK_OFFENSE_LIMIT) {
      // The user's rule: "more than 3 times → penalty", regardless of location.
      p.punchKickOffenses[fouler] = 0;
      addStoppage(state, STOPPAGE_FOUL_S);
      clearPendingWhistle(state.match); // play stops here; drop any held foul
      const foulerTeam = p.team[fouler]!;
      beginRestart(state, MatchPhase.Penalty, victimTeam, penaltySpotX(state, foulerTeam), 0);
      runVarCheck(state);
      return;
    }
  }

  // Offences 1..3: a direct free kick on the spot (or a penalty if in the box).
  awardFoulRestart(state, fouler, victimTeam, x, z);
}

/** Consume a keeper 6-second offence → indirect free kick to the opponent. (§5.5) */
function handleSixSecond(state: WorldState, ev: RuleEvent): void {
  // IFAB 2026/27: a keeper hand-holding beyond GK_HOLD_LIMIT_S (8s) concedes a
  // CORNER to the opponents, taken from the corner nearest where the keeper
  // was penalised — superseding the old 6-second indirect free kick. The event
  // keeps its historical 'SIX_SECOND' name for wire/client stability.
  // goalkeeper.ts already sets `team` to the opponent and (x,z) to the keeper.
  // beginRestart lifts the keeper's hold as it drops the ball on the spot.
  const keeperTeam = 1 - ev.team;
  const cornerX = defendGoalX(state, keeperTeam);
  const cornerZ = (ev.z ?? 0) >= 0
    ? state.config.pitchWidth * 0.5
    : -state.config.pitchWidth * 0.5;
  beginRestart(state, MatchPhase.Corner, ev.team, cornerX, cornerZ);
  addStoppage(state, STOPPAGE_FOUL_S);
}

// ---------------------------------------------------------------------------
// Advantage window
// ---------------------------------------------------------------------------

/**
 * Tick down a live advantage. If the fouled team loses the ball before the
 * window closes, pull it back to the original free kick / penalty (§5.3); if the
 * window elapses with the attack intact, advantage is complete and the pending
 * restart is dropped.
 */
function runAdvantage(state: WorldState, out: StepResult): void {
  const m = state.match;
  if (m.advantageTicks <= 0 || m.phase !== MatchPhase.InPlay) return;
  m.advantageTicks--;

  const victimTeam = m.restartTeam;
  if (!teamInPossession(state, victimTeam)) {
    // Attack broke down → bring it back.
    awardFoulRestart(state, m.pendingFoulPlayer, victimTeam, m.restartX, m.restartZ);
    out.events.push({
      type: 'FOUL',
      tick: state.tick,
      team: victimTeam,
      player: m.pendingFoulPlayer,
      severity: m.pendingFoulSeverity,
      // NO card: the card was issued and reported on the ORIGINAL foul event,
      // ADVANTAGE_WINDOW_S ago. This event announces the pull-back, not a new
      // booking. (A consumer still deriving its card graphic from `severity`
      // will draw a duplicate here — it always has; `card` is how it stops.)
      card: RefereeCard.None,
      x: m.restartX,
      z: m.restartZ,
    });
    return;
  }
  if (m.advantageTicks === 0) {
    // Advantage seen through — no set piece.
    m.pendingFoulPlayer = -1;
    m.pendingFoulSeverity = 0;
  }
}

// ---------------------------------------------------------------------------
// Handball (§5.5) — a guard, not a hot path.
//
// Field players have no arm collider in this POD model, and goalkeeper.ts
// refuses to handle the ball outside its own box (that offence is deferred to
// rules/), so in normal play this never fires. It exists so that if a keeper is
// ever observed to have gained a hand-hold outside its area — a bug, or a future
// human-keeper path — the deliberate handball is punished (a red per §5.5) and a
// direct free kick is awarded, rather than silently allowed.
// ---------------------------------------------------------------------------

function evaluateHandball(state: WorldState, out: StepResult): void {
  const p = state.players;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (p.active[i] !== 1 || p.sentOff[i] === 1) continue;
    if (state.positions[i] !== 'GK') continue;
    // Grabbed the ball THIS tick and is outside its own penalty area.
    if (p.gkHoldStartTick[i]! !== state.tick) continue;
    if (insideOwnPenaltyArea(state, p.team[i]!, p.px[i]!, p.pz[i]!)) continue;

    const victimTeam = 1 - p.team[i]!;
    out.events.push({
      type: 'HANDBALL',
      tick: state.tick,
      team: victimTeam,
      player: i,
      // §5.5 calls this a red on the books; cards are disabled (owner,
      // 2026-08-17), so it is reported the same honest way as any other
      // offence now — Excessive on severity (still the correct classification
      // for a deliberate handball), None on card. The free kick still stands.
      severity: FoulSeverity.Excessive,
      card: RefereeCard.None,
      x: p.px[i]!,
      z: p.pz[i]!,
    });
    clearPendingWhistle(state.match);
    beginRestart(state, MatchPhase.FreeKick, victimTeam, p.px[i]!, p.pz[i]!);
    return;
  }
}

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

/**
 * Apply the consequence of every foul-family offence detected this tick, plus
 * the advantage window and the handball guard.
 *
 * `config.foulsEnabled` gates the contact-foul family (§5.3): small-sided casual
 * modes turn it off (§9.2). player-controller.ts still emits FOUL events without
 * consulting the flag and step.ts still calls us (its guard is `foulsEnabled ||
 * live`), so honouring the toggle is THIS module's job — an un-actioned FOUL
 * event is simply informational when fouls are off. The keeper 6-second rule is
 * a separate law (§5.5) and is not gated by it.
 */
export function evaluateFoulsAndCards(state: WorldState, out: StepResult): void {
  const foulsOn = state.config.foulsEnabled;

  // 0. A foul the referee already decided to give may become the whistle THIS
  //    tick (§5.6). First, so the tick a whistle lands is settled before any
  //    new offence is looked at.
  if (foulsOn) {
    runPendingWhistle(state);
    if (state.match.phase !== MatchPhase.InPlay) return;
  }

  // 1. A live advantage may pull play back before we look at anything new.
  if (foulsOn) {
    runAdvantage(state, out);
    if (state.match.phase !== MatchPhase.InPlay) return;
  }

  // 2. Consume offence events raised earlier this tick. Snapshot the length
  //    first so events we push (ADVANTAGE) are not reprocessed.
  const n = out.events.length;
  for (let e = 0; e < n; e++) {
    const ev = out.events[e]!;
    if (ev.type === 'FOUL' && foulsOn) {
      handleFoul(state, out, ev);
    } else if ((ev.type === 'PUNCH' || ev.type === 'KICK') && foulsOn) {
      // House rule: violent conduct — always a foul, with offence-counter escalation.
      handleViolentConduct(state, ev);
    } else if (ev.type === 'SIX_SECOND') {
      handleSixSecond(state, ev);
    }
    if (state.match.phase !== MatchPhase.InPlay) return; // play stopped — done
  }

  // 3. Handball guard (see note above) — part of the foul family.
  if (foulsOn) evaluateHandball(state, out);
}
